// src/components/dashboard/livegrid/AssetAiInsight.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — "FlowSense AI" section of the Live Grid asset drawer
//   • explainable priority (health, ML risk, predicted failure, live anomalies)
//   • ML finding + prescriptive action (repair vs replace) from the failure model
//   • "Ask FlowSense AI" → Ollama with the asset + grid snapshot (rules fallback)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, HStack, VStack, Text, Badge, Button, Icon, Tag, Wrap, WrapItem, useColorModeValue } from '@chakra-ui/react';
import { FaBrain, FaRobot } from 'react-icons/fa';
import { assessAsset, buildGridAiContext } from './liveGridIntel';
import { useFlowSenseAsk, SourceBadge } from './GridAiPanel';

const LEVEL_COLOR = { critical: 'red', high: 'orange', medium: 'yellow', low: 'green' };
const TYPE_LABEL = { emergency: 'Emergency', replacement: 'Replacement', predictive: 'Predictive maintenance', corrective: 'Corrective repair', inspection: 'Inspection' };

export default function AssetAiInsight({ asset, liveAlert, brief }) {
    const { ask, isAsking } = useFlowSenseAsk();
    const [answer, setAnswer] = useState(null);
    const bg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)');
    const border = useColorModeValue('purple.200', 'purple.700');
    const muted = useColorModeValue('gray.600', 'gray.400');

    // New asset → clear the previous answer
    const assetId = asset?.id;
    useEffect(() => { setAnswer(null); }, [assetId]);

    const insight = useMemo(() => (asset ? assessAsset(asset, Date.now(), liveAlert || null) : null), [asset, liveAlert]);
    if (!asset || !insight) return null;
    const rx = asset.prescriptive_action;

    const askAi = async () => {
        const ctx = buildGridAiContext(brief, asset);
        const q = `Assess the asset "${asset.name}" (${asset.type || 'asset'}). Explain its condition from the snapshot, the main risk, and the next action for the crew.`;
        const r = await ask(q, ctx, 'live_grid', () => ({
            answer: [`${asset.name}: ${insight.level} priority (score ${insight.score}/100).`, ...insight.reasons.map(x => `• ${x}`), `Recommendation: ${insight.recommendation}`].join('\n'),
        }));
        setAnswer(r);
    };

    return (
        <Box p={6} bg={bg} borderBottomWidth="1px" borderColor={border} role="region" aria-label="FlowSense AI insight">
            <HStack mb={3} spacing={2}>
                <Icon as={FaRobot} color="purple.500" boxSize={3.5} />
                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wider" color="purple.500">FlowSense AI insight</Text>
                <Badge ml="auto" colorScheme={LEVEL_COLOR[insight.level]} aria-label={`AI priority ${insight.level}`}>{insight.level} · {insight.score}</Badge>
            </HStack>

            {insight.reasons.length ? (
                <VStack align="stretch" spacing={0.5} mb={2}>
                    {insight.reasons.map(r => <Text key={r} fontSize="xs">• {r}</Text>)}
                </VStack>
            ) : (
                <Text fontSize="xs" color={muted} mb={2}>No risk signals — health, ML risk and live telemetry look normal.</Text>
            )}
            <Text fontSize="sm" fontWeight="600" mb={2}>{insight.recommendation}</Text>
            <Wrap spacing={1} mb={3}>
                <WrapItem><Tag size="sm" colorScheme="purple">Suggested: {TYPE_LABEL[insight.suggestedType] || insight.suggestedType}</Tag></WrapItem>
                <WrapItem><Tag size="sm" colorScheme={LEVEL_COLOR[insight.suggestedPriority]}>Priority: {insight.suggestedPriority}</Tag></WrapItem>
                {insight.failInDays !== null && <WrapItem><Tag size="sm" colorScheme="pink">Fail in ≈ {Math.max(0, insight.failInDays)} d</Tag></WrapItem>}
            </Wrap>

            {(asset.risk_score != null || rx) && (
                <Box borderWidth="1px" borderColor={border} borderRadius="lg" p={3} mb={3}>
                    <HStack mb={1} spacing={2}>
                        <Icon as={FaBrain} color="purple.400" boxSize={3} />
                        <Text fontSize="xs" fontWeight="700">ML failure model</Text>
                        {asset.risk_score != null && <Badge colorScheme={asset.risk_score >= 80 ? 'red' : asset.risk_score >= 60 ? 'orange' : 'yellow'} ml="auto">{asset.risk_score}% risk</Badge>}
                    </HStack>
                    {asset.risk_description && <Text fontSize="xs" color={muted}>{asset.risk_description}</Text>}
                    {rx?.action && <Text fontSize="xs" mt={1}><b>Prescription:</b> {rx.action}{rx.reason ? ` — ${rx.reason}` : ''}</Text>}
                </Box>
            )}

            <Button size="sm" w="100%" colorScheme="purple" variant="outline" leftIcon={<Icon as={FaRobot} />} onClick={askAi} isLoading={isAsking} loadingText="Asking FlowSense AI…">
                Ask FlowSense AI about this asset
            </Button>
            {answer && (
                <Box mt={3} p={3} borderRadius="lg" borderWidth="1px" borderColor={border} aria-live="polite">
                    <HStack justify="flex-end" mb={1}><SourceBadge source={answer.source} model={answer.model} note={answer.note} /></HStack>
                    <Text fontSize="sm" whiteSpace="pre-wrap">{answer.answer}</Text>
                </Box>
            )}
        </Box>
    );
}

AssetAiInsight.propTypes = { asset: PropTypes.object, liveAlert: PropTypes.object, brief: PropTypes.object };
